/**
 * cartEvents.js - Custom event system for cart updates
 * 
 * This module provides a simple pub/sub system for cart-related events
 * to ensure real-time updates across components without requiring
 * prop drilling or complex state management.
 */

// Event names
export const CART_EVENTS = {
  ITEM_ADDED: 'cart:item_added',
  ITEM_REMOVED: 'cart:item_removed',
  CART_UPDATED: 'cart:updated',
  CHECKOUT_COMPLETED: 'cart:checkout_completed'
};

/**
 * Dispatch a cart update event
 * @param {string} eventType - Type of cart event (use CART_EVENTS constants)
 * @param {object} detail - Additional data to include with the event
 */
export const dispatchCartEvent = (eventType, detail = {}) => {
  console.debug(`Dispatching cart event: ${eventType}`, detail);
  
  // Create and dispatch a custom event
  const event = new CustomEvent(eventType, { 
    detail,
    bubbles: true, 
    cancelable: true 
  });
  
  document.dispatchEvent(event);
};

/**
 * Listen for cart events
 * @param {string} eventType - Type of cart event to listen for
 * @param {function} callback - Function to call when event is triggered
 * @returns {function} - Function to remove the event listener
 */
export const listenToCartEvents = (eventType, callback) => {
  const handler = (event) => callback(event.detail);
  document.addEventListener(eventType, handler);
  
  // Return a function to remove the listener
  return () => document.removeEventListener(eventType, handler);
};

/**
 * Helper to listen to any cart update event
 * @param {function} callback - Function to call when any cart event is triggered
 * @returns {function} - Function to remove all event listeners
 */
export const listenToAllCartEvents = (callback) => {
  const handlers = [];
  
  // Add listeners for all event types
  Object.values(CART_EVENTS).forEach(eventType => {
    const handler = (event) => callback(event.detail, eventType);
    document.addEventListener(eventType, handler);
    handlers.push({ eventType, handler });
  });
  
  // Return a function to remove all listeners
  return () => {
    handlers.forEach(({ eventType, handler }) => {
      document.removeEventListener(eventType, handler);
    });
  };
};
